// src/lib/services/liveNavigation.ts
import { LiveRole } from '../../live/types/liveRole';
import { resolveLiveAudienceContract } from '../../live/types/liveAudience';
import type {
  LiveAudienceContract,
  LiveParticipantEntry,
  LiveViewerEntry,
} from '../../live/types/liveAudience';

export interface LiveNavigationTargetInput {
  id: string;
  source: 'native' | 'shorts' | 'meeting' | 'event' | 'youtube' | 'battle' | 'audio';
  shortsMode?: 'broadcast' | 'interactive';
  broadcastMeeting?: boolean;
  audienceContract?: LiveAudienceContract;
}

export type LiveNavigationEntry = LiveViewerEntry | LiveParticipantEntry;

export function getLiveNavigationEntry(
  live: LiveNavigationTargetInput,
  role: LiveRole = 'viewer',
): LiveNavigationEntry {
  const contract = resolveLiveAudienceContract({
    source: live.source,
    shortsMode: live.shortsMode,
    broadcastMeeting: live.broadcastMeeting,
    audienceContract: live.audienceContract,
  });
  return role === 'viewer' ? contract.viewerEntry : contract.participantEntry;
}

/**
 * Returns the correct navigation target for a live entry.
 *
 * Role routing rules (enforced here AND at the page level):
 *   native          → always /video/:id  (broadcast only)
 *   event           → host/invited → /event/:id  |  viewer → /video/:id
 *   meeting         → host/invited → /live/:id   |  viewer (broadcastMeeting) → /video/:id
 *   shorts/broadcast → /shorts?live=:id  (all roles)
 *   shorts/interactive, audio, battle → /live/:id  (all roles — Worker enforces access)
 */
export function getLiveNavigationTarget(
  live: LiveNavigationTargetInput,
  role: LiveRole = 'viewer',
): string {
  const targetEntry = getLiveNavigationEntry(live, role);

  if (targetEntry === 'watch_player') {
    return `/video/${live.id}`;
  }

  if (targetEntry === 'event_stage') {
    return `/event/${live.id}`;
  }

  if (targetEntry === 'shorts_feed') {
    return `/shorts?live=${encodeURIComponent(live.id)}`;
  }

  return `/live/${live.id}`;
}
